import React from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getProjectEscalations, updateProjectEscalations } from '../../../../../helpers/project-helper';

const extractEscalationIds = (escalations = []) => {
    if (!Array.isArray(escalations)) {
        return [];
    }

    return escalations
        .map((user) => (typeof user === 'object' ? user?._id : user))
        .filter(Boolean);
};

export const ProjectEscalationsTabContent = ({ projectId }) => {
    const { token } = useSelector((state) => state.auth);

    const [users, setUsers] = React.useState([]);
    const [projectEscalations, setProjectEscalations] = React.useState([]);
    const [selectedUserIds, setSelectedUserIds] = React.useState([]);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        const loadEscalations = async () => {
            if (!token || !projectId) {
                setError('No se pudo obtener la configuración de escalaciones del proyecto.');
                setUsers([]);
                setProjectEscalations([]);
                setSelectedUserIds([]);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError('');

            const resp = await getProjectEscalations(token, projectId);

            if (!resp?.success) {
                setError(resp?.message || 'No se pudo obtener la configuración de escalaciones del proyecto.');
                setUsers([]);
                setProjectEscalations([]);
                setSelectedUserIds([]);
                setIsLoading(false);
                return;
            }

            const nextUsers = Array.isArray(resp?.data?.users) ? resp.data.users : [];
            const nextEscalations = Array.isArray(resp?.data?.escalations) ? resp.data.escalations : [];

            setUsers(nextUsers);
            setProjectEscalations(nextEscalations);
            setSelectedUserIds(extractEscalationIds(nextEscalations));
            setIsLoading(false);
        };

        loadEscalations();
    }, [token, projectId]);

    const usersById = React.useMemo(() => {
        return users.reduce((accumulator, user) => {
            if (!user?._id) {
                return accumulator;
            }

            return {
                ...accumulator,
                [user._id]: user,
            };
        }, {});
    }, [users]);

    const projectEscalationsById = React.useMemo(() => {
        return projectEscalations.reduce((accumulator, user) => {
            if (!user?._id) {
                return accumulator;
            }

            return {
                ...accumulator,
                [user._id]: user,
            };
        }, {});
    }, [projectEscalations]);

    const selectedUsers = React.useMemo(() => {
        return selectedUserIds.map((userId) => {
            const existingUser = usersById[userId] || projectEscalationsById[userId];

            if (existingUser) {
                return existingUser;
            }

            return {
                _id: userId,
                name: 'Usuario no disponible',
                email: '-',
                role: '-',
            };
        });
    }, [selectedUserIds, usersById, projectEscalationsById]);

    const filteredUsers = React.useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        const selectedSet = new Set(selectedUserIds);

        if (!normalizedSearch) {
            return [];
        }

        return users
            .filter((user) => {
                const userId = user?._id;

                if (!userId || selectedSet.has(userId)) {
                    return false;
                }

                const normalizedName = String(user?.name || '').toLowerCase();
                const normalizedEmail = String(user?.email || '').toLowerCase();
                const normalizedRole = String(user?.role || '').toLowerCase();

                return normalizedName.includes(normalizedSearch)
                    || normalizedEmail.includes(normalizedSearch)
                    || normalizedRole.includes(normalizedSearch);
            })
            .slice(0, 20);
    }, [users, selectedUserIds, searchTerm]);

    const handleAddUser = (userId) => {
        setSelectedUserIds((prev) => {
            if (prev.includes(userId)) {
                return prev;
            }

            return [...prev, userId];
        });
        setSearchTerm('');
    };

    const handleRemoveUser = (userId) => {
        setSelectedUserIds((prev) => prev.filter((item) => item !== userId));
    };

    const handleSaveEscalations = async () => {
        if (!token || !projectId || isSaving) {
            return;
        }

        setIsSaving(true);
        setError('');
        const savingToastId = toast.loading('Guardando escalaciones del proyecto...');

        const resp = await updateProjectEscalations(token, projectId, {
            escalations: selectedUserIds,
        });

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo actualizar la configuración de escalaciones del proyecto.';
            setError(message);
            toast.update(savingToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsSaving(false);
            return;
        }

        const nextEscalations = Array.isArray(resp?.data?.escalations) ? resp.data.escalations : [];

        setProjectEscalations(nextEscalations);
        setSelectedUserIds(extractEscalationIds(nextEscalations));
        toast.update(savingToastId, {
            render: resp?.message || 'Escalaciones actualizadas correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
        setIsSaving(false);
    };

    return (
        <div>
            <div className="mb-4 d-flex align-items-center justify-content-between">
                <h5 className="fw-bold mb-0">Usuarios para escalación de tickets</h5>
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveEscalations}
                    disabled={isSaving || isLoading}
                >
                    {isSaving ? 'Guardando...' : 'Guardar escalaciones'}
                </button>
            </div>

            {isLoading && <div className="py-3 text-muted">Cargando escalaciones...</div>}

            {!isLoading && error && (
                <div className="alert alert-danger py-2" role="alert">{error}</div>
            )}

            {!isLoading && !error && users.length === 0 && (
                <div className="py-3 text-muted">No hay usuarios activos disponibles en el tenant.</div>
            )}

            {!isLoading && !error && users.length > 0 && (
                <>
                    <div className="mb-4">
                        <label htmlFor="escalation-user-search" className="form-label">Buscar usuario</label>
                        <input
                            id="escalation-user-search"
                            type="text"
                            className="form-control"
                            placeholder="Escribe nombre, email o rol"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />

                        {searchTerm.trim().length > 0 && (
                            <div className="border border-gray-3 rounded-2 mt-2">
                                {filteredUsers.length === 0 && (
                                    <div className="p-2 text-muted">Sin resultados para esta búsqueda.</div>
                                )}

                                {filteredUsers.length > 0 && (
                                    <div className="list-group list-group-flush">
                                        {filteredUsers.map((user) => (
                                            <button
                                                key={user._id}
                                                type="button"
                                                className="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
                                                onClick={() => handleAddUser(user._id)}
                                            >
                                                <span>{user?.name || '-'} ({user?.email || '-'})</span>
                                                <span className="badge bg-soft-primary text-primary">Agregar</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {selectedUsers.length === 0 && (
                        <div className="py-2 text-muted">No hay usuarios seleccionados para escalación.</div>
                    )}

                    {selectedUsers.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-bordered mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Nombre</th>
                                        <th>Email</th>
                                        <th>Rol</th>
                                        <th style={{ width: '90px' }}>Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedUsers.map((user) => {
                                        const userId = user?._id;
                                        return (
                                            <tr key={userId}>
                                                <td>{user?.name || '-'}</td>
                                                <td>{user?.email || '-'}</td>
                                                <td>{user?.role || '-'}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() => handleRemoveUser(userId)}
                                                    >
                                                        Quitar
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
